/**
 * A baptism entry's later sacraments (owner, 2026-10-08; schema 13): the margin note of a
 * baptism register, Komuni Pertama, Krisma and Perkawinan, each a date and a parish, the
 * marriage also the spouse.
 *
 * Two sources, never one copied into the other (one place per fact):
 *
 * - What the clerk typed on the entry (`baptis.komuni_*`, `krisma_*`, `perkawinan_*`, in Ubah or
 *   in a ceremony's Baptis row): `ketik`.
 * - What SATU records of the person the entry is linked to (`baptis.umat_id`, or a person whose
 *   `baptis_id` names it): their linked Komuni Pertama and Krisma entries and their latest valid
 *   marriage in SATU's books, else the sacrament received in another parish on their record
 *   (`referensi_sakramen_luar`): `otomatis`. The device reads it when it shows or prints the
 *   entry (apps/web/src/db/queries/sakramenLanjut.ts), so a Komuni entry saved, corrected,
 *   cancelled or unlinked shows on the baptism at once, on every device, whatever app wrote it.
 *
 * A sacrament shows (and prints) what was typed, else what SATU records. Typed and recorded both
 * there but different: the typed one stays, never replaced, and the difference is said beside it
 * for the clerk to keep it or to clear it ("Pakai yang tercatat"). A source cancelled or unlinked
 * takes its value away, never a typed one. Pure.
 */
import { formatPartialDateId, isPartialDate, partialDateEarliest } from './dates/partial'
import { namaParokiLengkap } from './namaParoki'
import { tanpaKataParoki } from './pelayan'
import { JUDUL_BUKU, type TabelRegister } from './registers'
import { format, suratStrings, uiStrings } from './strings/index'
import { catatanMencatatPerkawinan } from './surat'
import type { CatatanPinggir } from './validators/kolom'

export const SAKRAMEN_LANJUT = ['komuni', 'krisma', 'perkawinan'] as const
export type SakramenLanjut = (typeof SAKRAMEN_LANJUT)[number]

/** The entry's columns of each later sacrament. */
export const KOLOM_SAKRAMEN_LANJUT: Readonly<
  Record<SakramenLanjut, { tanggal: string; paroki: string; pasangan?: string }>
> = {
  komuni: { tanggal: 'komuni_tanggal', paroki: 'komuni_paroki' },
  krisma: { tanggal: 'krisma_tanggal', paroki: 'krisma_paroki' },
  perkawinan: {
    tanggal: 'perkawinan_tanggal',
    paroki: 'perkawinan_paroki',
    pasangan: 'perkawinan_pasangan',
  },
}

/** The register each later sacrament is recorded in. */
export const TABEL_SAKRAMEN_LANJUT: Readonly<Record<SakramenLanjut, TabelRegister>> = {
  komuni: 'komuni_pertama',
  krisma: 'krisma',
  perkawinan: 'perkawinan',
}

/** One later sacrament as noted: its date (partial, canonical), its parish as written, a spouse. */
export interface CatatanSakramen {
  tanggal: string | null
  paroki: string | null
  /** The marriage's spouse; absent or null on the other two. */
  pasangan?: string | null
}

/** What SATU records of the person: from an entry of its books, or their outside reference. */
export interface SakramenTercatat extends CatatanSakramen {
  asal: 'buku' | 'luar'
}

export interface BarisSakramenLanjut {
  sakramen: SakramenLanjut
  /** What the entry shows and prints; null when nothing is typed and nothing recorded. */
  tampil: CatatanSakramen | null
  /** Where `tampil` comes from. */
  asal: 'ketik' | 'buku' | 'luar' | null
  /** What was typed on the entry; null when nothing was. */
  ketik: CatatanSakramen | null
  /** What SATU records; null when nothing. */
  tercatat: SakramenTercatat | null
  /** Both there and different: the typed one is shown, this one is said beside it. */
  beda: boolean
}

type Baris = Readonly<Record<string, unknown>>

function isi(value: unknown): string | null {
  if (typeof value !== 'string') return null
  const v = value.trim()
  return v === '' ? null : v
}

/**
 * Has a sacrament dated `tanggal` been received by `hariIni` (canonical, WIT)? An entry may be
 * dated up to a year ahead, to prepare its certificate before the ceremony (release 17, B2); it
 * is not received yet, so no letter states it (release 17 review I-2). A partial date counts from
 * its first possible day ("2026-10" from 1 October, "2026" from 1 January): an old book's partial
 * date is in the past, and a date that may be today is not refused. No date counts (a copied
 * book that does not record it).
 */
export function sudahTerjadi(tanggal: string | null | undefined, hariIni: string): boolean {
  if (tanggal === null || tanggal === undefined || tanggal.trim() === '') return true
  if (!isPartialDate(tanggal) || hariIni === '') return true
  return partialDateEarliest(tanggal) <= hariIni
}

/** What was typed for one sacrament; null when none of its columns holds anything. */
export function ketikSakramen(baptis: Baris, sakramen: SakramenLanjut): CatatanSakramen | null {
  const kolom = KOLOM_SAKRAMEN_LANJUT[sakramen]
  const catatan: CatatanSakramen = {
    tanggal: isi(baptis[kolom.tanggal]),
    paroki: isi(baptis[kolom.paroki]),
  }
  if (kolom.pasangan !== undefined) catatan.pasangan = isi(baptis[kolom.pasangan])
  return kosong(catatan) ? null : catatan
}

function kosong(c: CatatanSakramen): boolean {
  return c.tanggal === null && c.paroki === null && (c.pasangan ?? null) === null
}

const kunciParoki = (v: string | null): string =>
  v === null ? '' : tanpaKataParoki(v).replace(/\s+/g, ' ').toLocaleLowerCase('id')
const kunciNama = (v: string | null | undefined): string =>
  (v ?? '').trim().replace(/\s+/g, ' ').toLocaleLowerCase('id')

/** The same note: one date, one parish (with or without "Paroki", any case), one spouse. */
export function samaCatatan(a: CatatanSakramen, b: CatatanSakramen): boolean {
  return (
    (a.tanggal ?? '') === (b.tanggal ?? '') &&
    kunciParoki(a.paroki) === kunciParoki(b.paroki) &&
    kunciNama(a.pasangan) === kunciNama(b.pasangan)
  )
}

/** Each later sacrament of a baptism entry (its stored row) with what SATU records of the person. */
export function sakramenLanjut(
  baptis: Baris,
  tercatat: Readonly<Partial<Record<SakramenLanjut, SakramenTercatat | null>>>,
): BarisSakramenLanjut[] {
  return SAKRAMEN_LANJUT.map((sakramen) => {
    const ketik = ketikSakramen(baptis, sakramen)
    const dariSatu = tercatat[sakramen] ?? null
    if (ketik !== null) {
      return {
        sakramen,
        tampil: ketik,
        asal: 'ketik',
        ketik,
        tercatat: dariSatu,
        beda: dariSatu !== null && !samaCatatan(ketik, dariSatu),
      }
    }
    return {
      sakramen,
      tampil: dariSatu,
      asal: dariSatu?.asal ?? null,
      ketik: null,
      tercatat: dariSatu,
      beda: false,
    }
  })
}

/** "12 Mei 2010, Paroki Katedral Salib Suci Agats": the date and the parish, whichever is there. */
function tanggalDanParoki(c: CatatanSakramen): string {
  return [
    c.tanggal === null ? null : formatPartialDateId(c.tanggal, 'panjang'),
    c.paroki === null ? null : namaParokiLengkap(c.paroki),
  ]
    .filter((v): v is string => v !== null && v !== '')
    .join(', ')
}

/**
 * The note as a screen says it under the sacrament's name: "12 Mei 2010, Paroki Katedral Salib
 * Suci Agats"; a marriage starts with the spouse ("Maria Kamsa, 1 Juni 2019, Paroki …").
 */
export function rincianSakramen(sakramen: SakramenLanjut, c: CatatanSakramen): string {
  const pasangan = sakramen === 'perkawinan' ? isi(c.pasangan) : null
  return [pasangan, tanggalDanParoki(c)].filter((v) => v !== null && v !== '').join(', ')
}

/**
 * The note as the Surat Baptis prints it (owner, 2026-10-08): "Komuni Pertama: 12 Mei 2010,
 * Paroki Katedral Salib Suci Agats.", "Krisma: …", "Menikah dengan Maria Kamsa: 1 Juni 2019,
 * Paroki …". Whatever is missing is left out ("Menikah: 1 Juni 2019." without a spouse); the
 * parish is named once ("Paroki" never doubled).
 */
export function kalimatSakramen(sakramen: SakramenLanjut, c: CatatanSakramen): string {
  const t = suratStrings.baptis.sakramen
  const pasangan = isi(c.pasangan)
  const kepala =
    sakramen === 'komuni'
      ? t.komuni
      : sakramen === 'krisma'
        ? t.krisma
        : pasangan === null
          ? t.menikah
          : format(t.menikahDengan, { pasangan })
  const rincian = tanggalDanParoki(c)
  return `${kepala}${rincian === '' ? '' : `: ${rincian}`}.`
}

/**
 * The printed notes of an entry, in the book's order (Komuni, Krisma, Perkawinan), those present.
 * A marriage is stated once (release 17 review M-6): when a margin note of the entry already records
 * it (`catatanMencatatPerkawinan`, "menikah dengan …"), that note, printed under its date, is its
 * statement, and the Perkawinan line is left out, whether typed or recorded.
 */
export function kalimatSakramenLanjut(
  baris: readonly BarisSakramenLanjut[],
  catatanPinggir: readonly CatatanPinggir[] = [],
): string[] {
  const nikahDiCatatan = catatanMencatatPerkawinan(catatanPinggir)
  return baris.flatMap((b) =>
    b.tampil === null || (b.sakramen === 'perkawinan' && nikahDiCatatan)
      ? []
      : [kalimatSakramen(b.sakramen, b.tampil)],
  )
}

/** Where a shown note comes from, as the entry's screen says it. */
export function asalSakramen(b: BarisSakramenLanjut): string | null {
  const t = uiStrings.buku.sakramenLanjut.asal
  if (b.asal === 'ketik') return t.ketik
  if (b.asal === 'buku')
    return format(t.buku, { buku: JUDUL_BUKU[TABEL_SAKRAMEN_LANJUT[b.sakramen]] })
  if (b.asal === 'luar') return t.luar
  return null
}

/**
 * The difference beside a typed note: "Buku Komuni Pertama mencatat: 3 Juni 2010, Paroki …"; null
 * when there is none.
 */
export function kalimatBeda(b: BarisSakramenLanjut): string | null {
  if (!b.beda || b.tercatat === null) return null
  const t = uiStrings.buku.sakramenLanjut
  return format(t.beda, {
    sumber:
      b.tercatat.asal === 'buku' ? JUDUL_BUKU[TABEL_SAKRAMEN_LANJUT[b.sakramen]] : t.sumberLuar,
    isi: rincianSakramen(b.sakramen, b.tercatat),
  })
}

/** "Pakai yang tercatat": the write that clears what was typed for one sacrament. */
export function kolomHapusKetik(sakramen: SakramenLanjut): Record<string, null> {
  const kolom = KOLOM_SAKRAMEN_LANJUT[sakramen]
  const out: Record<string, null> = { [kolom.tanggal]: null, [kolom.paroki]: null }
  if (kolom.pasangan !== undefined) out[kolom.pasangan] = null
  return out
}
